import { ActionList, TopBar, Banner } from "@shopify/polaris"
import React from "react"
import { useTranslation } from "react-i18next"
import { useAppContext } from "components/AppContext"
import "stylesheets/TopBarCustom.scss"
import TopBarSecondaryMenu from "components/widgets/TopBar/TopBarSecondaryMenu"
import LightOrganizationOrganism from "views/organisms/interactive/LightOrganizationOrganism"
import useAdminRules from "hooks/useAdminRules"
import TopBarUserMenu from "components/widgets/TopBar/TopBarUserMenu"
import useSearch from "hooks/useSearch"
import "stylesheets/blocs/molecules/organization/charts/ActionsOrganizationChartMolecule.scss"
import { useEntity } from "hooks/useAxios"
import NodeEntity from "services/entities/NodeEntity"
import ProductNavigation from "views/organisms/navigation/ProductNavigation"
import SearchFiltersDropdown from "./TopBar/SearchFiltersDropdown"
import SortDropdown from "./TopBar/SortDropdown"
import { useLocation } from "react-router-dom"

const SEARCH_LIST_ID = "topBarCustom__search"

const TopBarCustom = () => {
  const { t } = useTranslation()
  const { isUserOverallAdmin } = useAdminRules()
  const {
    updateCurrentNode,
    currentOrganization,
    currentUser,
    currentRootNode,
    treeFilters,
    tree,
    hasActiveFilters,
    isTreeLoading,
    treeSort,
  } = useAppContext()

  const location = useLocation()
  const isOrganizePage = location.pathname.includes("/organize")

  const nodeEntity = useEntity(NodeEntity)

  const {
    isError,
    onFocus,
    searchValue,
    isSearchActive,
    handleSearchChange,
    handleSearchResultsDismiss,
    nodes,
  } = useSearch({
    searchListId: SEARCH_LIST_ID,
    searchRequester: nodeEntity.search,
    onNodeAction: async (node) => await updateCurrentNode(node.id),
  })

  const hasEmptyFilterResults = React.useMemo(() => {
    if (!tree || !hasActiveFilters || isTreeLoading) return false

    const hasChildren = tree.children && tree.children.length > 0
    const hasOrganizations = tree.organizations && tree.organizations.length > 0
    const treeHasNodes = hasChildren || hasOrganizations

    return hasActiveFilters && !treeHasNodes
  }, [tree, hasActiveFilters, isTreeLoading])
  React.useMemo(() => {
    if (!hasActiveFilters) return ""

    const parts = []
    if (treeFilters.search) parts.push(`"${treeFilters.search}"`)
    if (treeFilters.scored) parts.push(t("dashboard:chart.filters.scored"))
    if (treeFilters.open) parts.push(t("dashboard:chart.filters.open"))
    if (treeFilters.guests) parts.push(t("dashboard:chart.filters.guests"))

    return parts.join(" + ")
  }, [hasActiveFilters, treeFilters, t])
  const searchFieldMarkup = isOrganizePage ? (
    <div className="custom-search-container">
      <div className="search-bar-wrapper">
        <div className="search-input-container">
          <TopBar.SearchField
            onChange={handleSearchChange}
            value={searchValue}
            placeholder={t("common:topbar.search")}
            showFocusBorder={false}
            focused={isSearchActive}
          />
        </div>

        {/* Filter and Sort buttons */}
        <div className="search-actions">
          <div
            className={`search-action-button ${
              treeSort?.field ? "active-sort" : ""
            }`}
          >
            <SortDropdown />
          </div>

          <div
            className={`search-action-button ${
              hasActiveFilters ? "active-filter" : ""
            }`}
          >
            <SearchFiltersDropdown />
            {hasActiveFilters && <div className="active-dot" />}
          </div>
        </div>
      </div>

      {hasEmptyFilterResults && (
        <div className="empty-filter-banner">
          <Banner status="info" title={t("common:topbar.noFilterResults")}>
            <p>{t("common:topbar.noFilterResultsDescription")}</p>
          </Banner>
        </div>
      )}
    </div>
  ) : (
    <TopBar.SearchField
      onChange={handleSearchChange}
      value={searchValue}
      placeholder={t("common:topbar.search")}
      showFocusBorder={false}
      onFocus={onFocus}
      focused={isSearchActive}
    />
  )

  const noResultsAction = [
    {
      content: t("common:topbar.noResults"),
      disabled: true,
    },
  ]

  const errorAction = [
    {
      content: t("common:topbar.searchError"),
      disabled: true,
    },
  ]

  const displayedNodes = nodes.length ? nodes : noResultsAction

  const searchResultsMarkup = (
    <ActionList
      actionRole={SEARCH_LIST_ID}
      items={isError ? errorAction : displayedNodes}
    />
  )

  const showAddCompanyButton =
    !!currentRootNode?.is_provider && !currentOrganization?.is_partial_partner

  const searchProps = !!currentUser && {
    onSearchResultsDismiss: handleSearchResultsDismiss,
    searchField: searchFieldMarkup,
    searchResults: searchResultsMarkup,
    searchResultsVisible: isSearchActive,
  }

  return (
    <TopBar
      showNavigationToggle
      userMenu={<TopBarUserMenu />}
      secondaryMenu={
        <div className="topBarCustom__activator topBarCustom__activator--max-width">
          {showAddCompanyButton && isUserOverallAdmin && (
            <LightOrganizationOrganism
              disabled={currentOrganization?.is_max_tree_exceeded}
            />
          )}
          <TopBarSecondaryMenu />

          {!currentOrganization?.is_partial_partner && <ProductNavigation />}
        </div>
      }
      {...searchProps}
    />
  )
}

export default TopBarCustom
